var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useMemo } from 'react';
import moment from 'moment';
import { useWatch } from 'react-hook-form';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { computeMaxOccurrencesUnder } from '../components/RecurrenceDropdown/utils';
import { canBypassSpaceBookingPolicy } from '../utils/spaceBookingPolicyUtils';
var ABT_SLUG = 'space-reservation-advanced-booking-threshold';
var ABT_FLAG = 'chronos(disable-never-ending-recurrence-with-abt)';
/**
 * Pure helper — exported for unit testing.
 * Returns the shortest valid ISO 8601 duration found in any selected space's
 * ABT setting, or null if no space has a parseable duration. Spaces where the
 * user has `spaces:bypass_booking_policies` are excluded so the cap reflects
 * only the spaces that actually constrain this user.
 */
export function getShortestAbtDuration(spaces) {
    var e_1, _a;
    if (!spaces || spaces.length === 0)
        return null;
    var shortest = null;
    try {
        for (var spaces_1 = __values(spaces), spaces_1_1 = spaces_1.next(); !spaces_1_1.done; spaces_1_1 = spaces_1.next()) {
            var space = spaces_1_1.value;
            if (canBypassSpaceBookingPolicy(space))
                continue;
            var duration = parseSpaceAbtDuration(space);
            if (!duration)
                continue;
            if (!shortest || duration.asMilliseconds() < shortest.asMilliseconds()) {
                shortest = duration;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (spaces_1_1 && !spaces_1_1.done && (_a = spaces_1.return)) _a.call(spaces_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return shortest;
}
/**
 * Pure helper — exported for unit testing.
 * Returns now + shortest ABT duration across spaces, or null when no ABT
 * applies. The cutoff is anchored to "now" because the threshold caps how far
 * into the future a user is allowed to book — not how far past the event's
 * own start. `now` is injectable for deterministic testing.
 */
export function getAbtUntilDate(spaces, now) {
    if (now === void 0) { now = moment(); }
    var duration = getShortestAbtDuration(spaces);
    if (!duration)
        return null;
    return now.clone().add(duration);
}
function parseSpaceAbtDuration(space) {
    var _a;
    var setting = (_a = space.settings) === null || _a === void 0 ? void 0 : _a.find(function (s) { return s.slug === ABT_SLUG; });
    if (!(setting === null || setting === void 0 ? void 0 : setting.value))
        return null;
    var duration = moment.duration(setting.value);
    var ms = duration.asMilliseconds();
    if (!ms || ms <= 0 || !Number.isFinite(ms))
        return null;
    return duration;
}
/**
 * Returns true when the given rrule produces at least one occurrence after
 * `limit`. Never-ending rrules (no until, no count) always exceed any limit.
 * Count-bounded rrules are evaluated against `dateStart` so the Nth occurrence
 * can be located.
 */
export function rruleExceedsDate(rrule, limit, dateStart) {
    if (!rrule)
        return false;
    var opts = rrule.options;
    if (!opts.until && !opts.count)
        return true;
    if (opts.until)
        return moment(opts.until).isAfter(limit);
    if (dateStart.isAfter(limit))
        return true;
    if (opts.count) {
        var maxCount = computeMaxOccurrencesUnder({
            freq: opts.freq,
            interval: opts.interval,
            byweekday: opts.byweekday,
            dateStart: dateStart,
            until: limit,
        });
        return maxCount < opts.count;
    }
    return false;
}
/**
 * Pure helper — exported for unit testing.
 * Returns the spaces whose ABT cap is exceeded by the given rrule. A space
 * with no ABT setting is never in conflict; a space with a valid ABT is in
 * conflict iff the rrule has an occurrence after now + that space's duration
 * (or the rrule never ends). Spaces where the user has
 * `spaces:bypass_booking_policies` are never reported as conflicting.
 *
 * `dateStart` is the rrule's dtstart, used to reconstruct the rrule when
 * checking count-bounded last occurrences. `now` is the booking-cutoff
 * anchor and defaults to the current time; injectable for deterministic
 * testing.
 */
export function getSpacesWithAbtConflict(spaces, rrule, dateStart, now) {
    if (now === void 0) { now = moment(); }
    if (!rrule || !spaces || !dateStart)
        return [];
    return spaces.filter(function (space) {
        if (canBypassSpaceBookingPolicy(space))
            return false;
        var duration = parseSpaceAbtDuration(space);
        if (!duration)
            return false;
        var spaceUntil = now.clone().add(duration);
        return rruleExceedsDate(rrule, spaceUntil, dateStart);
    });
}
/**
 * Returns ABT-derived state for the event composer:
 *   - `abtUntilDate`: now + shortest ABT duration across selected spaces
 *     (used as the cap when the user picks a recurrence preset). The cap is
 *     anchored to "now" because the threshold caps how far in the future a
 *     user is allowed to book.
 *   - `conflictingSpaceIds`: spaces whose ABT is exceeded by the current
 *     `recurrenceRule` form value. Drives the per-space conflict indicator
 *     and the footer Save-button disable.
 *
 * Both are gated on the feature flag — when the flag is off, returns empty
 * defaults so legacy behavior is preserved.
 */
export function useAdvancedBookingThreshold() {
    var _a = __read(useWatch({ name: ['spaces', 'date', 'recurrenceRule'] }), 3), spaces = _a[0], dateStart = _a[1], recurrenceRule = _a[2];
    var _b = useFeatureFlags(), checkFlag = _b.checkFlag, loading = _b.loading;
    var flagEnabled = !loading && checkFlag(ABT_FLAG);
    var dateStartMs = dateStart ? dateStart.valueOf() : null;
    var abtKey = useMemo(function () {
        if (!spaces)
            return '';
        return spaces
            .map(function (s) { var _a, _b, _c; return (_c = (_b = (_a = s.settings) === null || _a === void 0 ? void 0 : _a.find(function (x) { return x.slug === ABT_SLUG; })) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : ''; })
            .join('|');
    }, [spaces]);
    var abtUntilDate = useMemo(function () {
        if (!flagEnabled)
            return null;
        return getAbtUntilDate(spaces);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagEnabled, abtKey]);
    var conflictingSpaceIds = useMemo(function () {
        if (!flagEnabled)
            return [];
        return getSpacesWithAbtConflict(spaces, recurrenceRule, dateStart).map(function (s) { return s.id; });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagEnabled, dateStartMs, abtKey, recurrenceRule]);
    return { abtUntilDate: abtUntilDate, conflictingSpaceIds: conflictingSpaceIds };
}
